// components/Navigation/UKEntitySelector/UKEntitySelector.js
import React, { useMemo, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import {
    toggleNetwork,
    toggleChain,
    selectNetwork,
    selectChain,
    toggleClub,
    deselectNetwork,
    deselectChain
} from '../../../../redux/slices/myGymTV/ukEntitiesSlice';
import QuickSelectClubs from '../QuickSelectClubs/QuickSelectClubs';
import './uk-entity-selector.scss';

const UKEntitySelector = ({
    standalone = true,
    initialSelectedClubIds = [],
    onSelectionChange = null,
    allVenues = null
}) => {
    const dispatch = useDispatch();
    const { ukEntityData: reduxUkEntityData } = useSelector((state) => state.user);
    const {
        selectedClubs: reduxSelectedClubs,
        expandedNetworks: reduxExpandedNetworks,
        expandedChains: reduxExpandedChains
    } = useSelector((state) => state.ukEntities);

    // Local state for controlled mode
    const [localSelectedClubs, setLocalSelectedClubs] = useState([]);
    const [localExpandedNetworks, setLocalExpandedNetworks] = useState([]);
    const [localExpandedChains, setLocalExpandedChains] = useState([]);

    // Use either Redux state (standalone) or local state (controlled)
    const ukEntityData = allVenues || reduxUkEntityData;
    const selectedClubs = standalone ? reduxSelectedClubs : localSelectedClubs;
    const expandedNetworks = standalone ? reduxExpandedNetworks : localExpandedNetworks;
    const expandedChains = standalone ? reduxExpandedChains : localExpandedChains;

    // Initialize local state in controlled mode
    useEffect(() => {
        if (!standalone && initialSelectedClubIds.length > 0) {
            const initialClubs = ukEntityData.filter(venue =>
                initialSelectedClubIds.includes(venue.ClubId)
            );
            setLocalSelectedClubs(initialClubs);
        }
    }, [standalone, initialSelectedClubIds, ukEntityData]);

    // Notify parent of selection changes in controlled mode
    useEffect(() => {
        if (!standalone && onSelectionChange) {
            onSelectionChange(localSelectedClubs.map(c => c.ClubId));
        }
    }, [localSelectedClubs, standalone, onSelectionChange]);

    // Build hierarchical structure from flat data
    const hierarchicalData = useMemo(() => {
        if (!ukEntityData || ukEntityData.length === 0) return [];

        const networks = {};

        ukEntityData.forEach((entity) => {
            if (!networks[entity.NetworkId]) {
                networks[entity.NetworkId] = {
                    NetworkId: entity.NetworkId,
                    NetworkName: entity.NetworkName,
                    chains: {}
                };
            }

            if (!networks[entity.NetworkId].chains[entity.ChainId]) {
                networks[entity.NetworkId].chains[entity.ChainId] = {
                    ChainId: entity.ChainId,
                    ChainName: entity.ChainName,
                    NetworkId: entity.NetworkId,
                    clubs: []
                };
            }

            networks[entity.NetworkId].chains[entity.ChainId].clubs.push(entity);
        });

        return Object.values(networks);
    }, [ukEntityData]);

    // Helper functions to check selection state
    const getNetworkSelectionState = (networkId) => {
        const networkClubs = ukEntityData.filter(c => c.NetworkId === networkId);
        const selectedCount = networkClubs.filter(club =>
            selectedClubs.some(sc => sc.ClubId === club.ClubId)
        ).length;

        if (selectedCount === 0) return 'none';
        if (selectedCount === networkClubs.length) return 'all';
        return 'some';
    };

    const getChainSelectionState = (chainId) => {
        const chainClubs = ukEntityData.filter(c => c.ChainId === chainId);
        const selectedCount = chainClubs.filter(club =>
            selectedClubs.some(sc => sc.ClubId === club.ClubId)
        ).length;

        if (selectedCount === 0) return 'none';
        if (selectedCount === chainClubs.length) return 'all';
        return 'some';
    };

    const isClubSelected = (clubId) => {
        return selectedClubs.some(c => c.ClubId === clubId);
    };

    const handleNetworkClick = (network) => {
        if (standalone) {
            dispatch(toggleNetwork({ networkId: network.NetworkId }));
        } else {
            setLocalExpandedNetworks(prev =>
                prev.includes(network.NetworkId)
                    ? prev.filter(id => id !== network.NetworkId)
                    : [...prev, network.NetworkId]
            );
        }
    };

    const handleNetworkCheckbox = (network, e) => {
        e.stopPropagation();

        const selectionState = getNetworkSelectionState(network.NetworkId);

        if (standalone) {
            if (selectionState === 'all') {
                dispatch(deselectNetwork(network.NetworkId));
            } else {
                dispatch(selectNetwork({
                    networkId: network.NetworkId,
                    allClubs: ukEntityData
                }));
            }
        } else {
            const networkClubs = ukEntityData.filter(c => c.NetworkId === network.NetworkId);
            if (selectionState === 'all') {
                setLocalSelectedClubs(prev =>
                    prev.filter(club => club.NetworkId !== network.NetworkId)
                );
            } else {
                setLocalSelectedClubs(prev => {
                    const filtered = prev.filter(club => club.NetworkId !== network.NetworkId);
                    return [...filtered, ...networkClubs];
                });
            }
        }
    };

    const handleChainClick = (chain) => {
        if (standalone) {
            dispatch(toggleChain({ chainId: chain.ChainId }));
        } else {
            setLocalExpandedChains(prev =>
                prev.includes(chain.ChainId)
                    ? prev.filter(id => id !== chain.ChainId)
                    : [...prev, chain.ChainId]
            );
        }
    };

    const handleChainCheckbox = (chain, e) => {
        e.stopPropagation();

        const selectionState = getChainSelectionState(chain.ChainId);

        if (standalone) {
            if (selectionState === 'all') {
                dispatch(deselectChain(chain.ChainId));
            } else {
                dispatch(selectChain({
                    chainId: chain.ChainId,
                    allClubs: ukEntityData
                }));
            }
        } else {
            const chainClubs = ukEntityData.filter(c => c.ChainId === chain.ChainId);
            if (selectionState === 'all') {
                setLocalSelectedClubs(prev =>
                    prev.filter(club => club.ChainId !== chain.ChainId)
                );
            } else {
                setLocalSelectedClubs(prev => {
                    const filtered = prev.filter(club => club.ChainId !== chain.ChainId);
                    return [...filtered, ...chainClubs];
                });
            }
        }
    };

    const handleClubCheckbox = (club, e) => {
        e.stopPropagation();

        if (standalone) {
            dispatch(toggleClub(club));
        } else {
            const isSelected = localSelectedClubs.some(c => c.ClubId === club.ClubId);
            if (isSelected) {
                setLocalSelectedClubs(prev => prev.filter(c => c.ClubId !== club.ClubId));
            } else {
                setLocalSelectedClubs(prev => [...prev, club]);
            }
        }
    };

    if (!ukEntityData || ukEntityData.length === 0) {
        return null;
    }

    return (
        <div className="uk-entity-selector mb-4">
            {standalone && (
                <div className="selector-header mb-2">
                    <h6 className="text-white mb-0">Select Locations</h6>
                </div>
            )}

            {standalone && <QuickSelectClubs />}

            {!standalone && (
                <div className="mb-3">
                    <strong>Selected: {selectedClubs.length} venues</strong>
                </div>
            )}

            <div className="entity-tree">
                {hierarchicalData.map((network) => {
                    const networkState = getNetworkSelectionState(network.NetworkId);

                    return (
                        <div
                            className="network-item"
                            key={network.NetworkId}
                        >
                            <div
                                className="entity-row network-row"
                                onClick={() => handleNetworkClick(network)}
                            >
                                <IndeterminateCheckbox
                                    checked={networkState === 'all'}
                                    indeterminate={networkState === 'some'}
                                    onChange={(e) => handleNetworkCheckbox(network, e)}
                                    onClick={(e) => e.stopPropagation()}
                                />
                                <FontAwesomeIcon
                                    className="expand-icon"
                                    icon={expandedNetworks.includes(network.NetworkId) ? faChevronDown : faChevronRight}
                                />
                                <span className="entity-name">{network.NetworkName}</span>
                            </div>

                            {expandedNetworks.includes(network.NetworkId) && (
                                <div className="chains-container">
                                    {Object.values(network.chains).map((chain) => {
                                        const chainState = getChainSelectionState(chain.ChainId);

                                        return (
                                            <div
                                                className="chain-item"
                                                key={chain.ChainId}
                                            >
                                                <div
                                                    className="entity-row chain-row"
                                                    onClick={() => handleChainClick(chain)}
                                                >
                                                    <IndeterminateCheckbox
                                                        checked={chainState === 'all'}
                                                        indeterminate={chainState === 'some'}
                                                        onChange={(e) => handleChainCheckbox(chain, e)}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                    <FontAwesomeIcon
                                                        className="expand-icon"
                                                        icon={expandedChains.includes(chain.ChainId) ? faChevronDown : faChevronRight}
                                                    />
                                                    <span className="entity-name">{chain.ChainName}</span>
                                                </div>

                                                {expandedChains.includes(chain.ChainId) && (
                                                    <div className="clubs-container">
                                                        {chain.clubs.map((club) => (
                                                            <div
                                                                className="club-item"
                                                                key={club.ClubId}
                                                            >
                                                                <div className="entity-row club-row">
                                                                    <input
                                                                        checked={isClubSelected(club.ClubId)}
                                                                        onChange={(e) => handleClubCheckbox(club, e)}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        type="checkbox"
                                                                    />
                                                                    <span className="entity-name">{club.ClubName}</span>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// Separate component for indeterminate checkbox
const IndeterminateCheckbox = ({ indeterminate, ...rest }) => {
    const ref = useRef(null);

    useEffect(() => {
        if (ref.current) {
            ref.current.indeterminate = indeterminate;
        }
    }, [indeterminate]);

    return <input
        ref={ref}
        type="checkbox"
        {...rest}
           />;
};

export default UKEntitySelector;